import { useCallback, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { normalizeLiveLogEnvelope } from '../services/logs.ts';
import type { LogEntry, LiveLogEnvelope } from '../types/logs.ts';

const MAX_LIVE_LOGS = 500;

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

function trimLogs(entries: LogEntry[]): LogEntry[] {
  return entries.length > MAX_LIVE_LOGS ? entries.slice(entries.length - MAX_LIVE_LOGS) : entries;
}

export const useLiveLogs = () => {
  const [rows, setRows] = useState<LogEntry[]>([]);
  const [paused, setPaused] = useState(false);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [bufferedCount, setBufferedCount] = useState(0);
  const pausedRef = useRef(false);
  const bufferedRef = useRef<LogEntry[]>([]);
  const counterRef = useRef(0);

  const appendLiveLog = useCallback((entry: LogEntry) => {
    if (pausedRef.current) {
      bufferedRef.current = trimLogs([...bufferedRef.current, entry]);
      setBufferedCount(bufferedRef.current.length);
      return;
    }
    setRows((prev) => trimLogs([...prev, entry]));
  }, []);

  const flushBuffered = useCallback(() => {
    if (bufferedRef.current.length === 0) {
      return;
    }
    setRows((prev) => trimLogs([...prev, ...bufferedRef.current]));
    bufferedRef.current = [];
    setBufferedCount(0);
  }, []);

  useEffect(() => {
    pausedRef.current = paused;
    if (!paused) {
      flushBuffered();
    }
  }, [flushBuffered, paused]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setStatus('disconnected');
      setError('No access token available for live logs');
      return;
    }

    setStatus('connecting');
    setError(null);

    const client = new Client({
      webSocketFactory: () => new SockJS('/ws'),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: 5000,
      debug: () => {},
      onConnect: () => {
        setStatus('connected');
        setError(null);
        client.subscribe('/topic/logs', (message) => {
          try {
            const payload = normalizeLiveLogEnvelope(
              JSON.parse(message.body) as LiveLogEnvelope,
              `live-${++counterRef.current}`
            );
            appendLiveLog(payload);
          } catch (err: any) {
            setError(err?.message || 'Failed to parse live log event');
          }
        });
      },
      onStompError: (frame) => {
        setStatus('error');
        setError(frame.headers?.message || 'STOMP connection failed');
      },
      onWebSocketClose: () => {
        setStatus('disconnected');
      },
      onWebSocketError: () => {
        setStatus('error');
        setError('WebSocket connection failed');
      },
    });

    client.activate();
    return () => {
      setStatus('disconnected');
      client.deactivate();
    };
  }, [appendLiveLog]);

  const togglePaused = useCallback(() => {
    setPaused((value) => !value);
  }, []);

  const clear = useCallback(() => {
    bufferedRef.current = [];
    setRows([]);
    setBufferedCount(0);
  }, []);

  return {
    rows,
    paused,
    status,
    error,
    bufferedCount,
    togglePaused,
    clear,
  };
};
